// src/pages/RideChatPage.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import socket from "../socket";
import RideBookingModal from "../components/RideBookingModal";

const API_BASE = process.env.REACT_APP_API_BASE;

// Normalize ANY ID → string
const normalizeId = (id) =>
  typeof id === "string"
    ? id
    : id?._id || id?.id || (id != null ? String(id) : "");

// Get customerId safely
const getCustomerId = () => {
  const direct = localStorage.getItem("customerId");
  if (direct && direct !== "null" && direct !== "undefined") return direct;

  const raw = localStorage.getItem("user");
  if (!raw) return "";
  try {
    const u = JSON.parse(raw);
    return u._id || u.id || "";
  } catch {
    return "";
  }
};

/* =========================================================
   🔥 Normalize booking status (new system)
========================================================= */
const getBookingStatus = (booking) => {
  if (!booking) return null;
  return (
    booking.requestStatus ||
    booking.rideStatus ||
    booking.status ||
    null
  );
};

const RideChatPage = () => {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const customerId = normalizeId(getCustomerId());

  // ⭐ STOP LOGIC RECEIVED FROM RIDE PREVIEW
  const stopFrom = location.state?.from || null;
  const stopTo = location.state?.to || null;
  const stopPrice = location.state?.price || null;
  const isStop = location.state?.isStop || false;

  const [ride, setRide] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roomId, setRoomId] = useState("");

  const bottomRef = useRef(null);

  /* =========================================================
     📡 Load ride + booking + chat
  ========================================================= */
  useEffect(() => {
    if (!rideId || !customerId) return;

    const loadData = async () => {
      try {
        const [rideRes, bookingRes, chatRes] = await Promise.all([
          axios.get(`${API_BASE}/api/ride/preview/${rideId}`),

          axios
            .get(
              `${API_BASE}/api/ridepayment/check-booking?rideId=${rideId}&customerId=${customerId}`
            )
            .catch(() => null),

          axios.get(`${API_BASE}/api/ride-chat/${rideId}?customerId=${customerId}`)
        ]);

        setRide(rideRes.data);
        if (bookingRes?.data?.booking || bookingRes?.data?.exists) {
          setBooking(bookingRes.data.booking);
        }

        setMessages(chatRes.data?.messages || []);

        const wid = normalizeId(
          rideRes.data.workerId?._id || rideRes.data.workerId
        );
        const room = `${rideId}-${customerId}-${wid}`;
        setRoomId(room);

        socket.emit("join-private-ride-chat", { roomId: room });
      } catch (err) {
        console.error("❌ Error loading chat:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [rideId, customerId]);

  /* =========================================================
     🔥 Listen for live messages
  ========================================================= */
  useEffect(() => {
    if (!roomId) return;

    const handler = (data) => {
      if (normalizeId(data.roomId) !== normalizeId(roomId)) return;
      const msg = data.message;

      setMessages((prev) => {
        const duplicate = prev.some(
          (m) =>
            m.timestamp === msg.timestamp &&
            m.text === msg.text &&
            normalizeId(m.senderId) === normalizeId(msg.senderId)
        );
        return duplicate ? prev : [...prev, msg];
      });
    };

    socket.on("ride-message", handler);
    return () => socket.off("ride-message", handler);
  }, [roomId]);

  /* =========================================================
     🔽 Auto scroll
  ========================================================= */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* =========================================================
     💬 Send message
  ========================================================= */
  const sendMessage = async () => {
    if (!newMsg.trim()) return;

    try {
      await axios.post(`${API_BASE}/api/ride-chat/send`, {
        rideId,
        roomId,
        sender: customerId,
        senderModel: "User",
        text: newMsg.trim()
      });
      setNewMsg("");
    } catch (err) {
      console.error("❌ Send message error:", err);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading chat…
      </div>
    );

  if (!ride)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        Ride not found
      </div>
    );

  /* =========================================================
     ⭐ PROPER BOOKING STATUS BUTTON
  ========================================================= */
  const bookingButton = () => {
    const st = getBookingStatus(booking);

    if (!st) {
      return (
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 rounded-full bg-green-600 text-white font-semibold shadow hover:bg-green-700"
        >
          Request Ride
        </button>
      );
    }

    if (st === "pending")
      return (
        <div className="px-4 py-2 rounded-full bg-yellow-100 text-yellow-700 font-semibold">
          ⏳ Booking Pending
        </div>
      );

    if (st === "accepted")
      return (
        <div className="px-4 py-2 rounded-full bg-green-100 text-green-700 font-semibold">
          ✅ Ride Confirmed
        </div>
      );

    if (st === "declined")
      return (
        <div className="px-4 py-2 rounded-full bg-red-100 text-red-700 font-semibold">
          ❌ Request Declined
        </div>
      );

    if (st === "completed")
      return (
        <div className="px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-semibold">
          🎉 Completed
        </div>
      );

    if (st === "refunded")
      return (
        <div className="px-4 py-2 rounded-full bg-purple-100 text-purple-700 font-semibold">
          💸 Refunded
        </div>
      );

    if (st === "disputed")
      return (
        <div className="px-4 py-2 rounded-full bg-orange-100 text-orange-700 font-semibold">
          ⚠️ Disputed
        </div>
      );

    return (
      <button
        onClick={() => setShowModal(true)}
        className="px-4 py-2 rounded-full bg-green-600 text-white font-semibold shadow hover:bg-green-700"
      >
        Request Ride
      </button>
    );
  };

  /* =========================================================
     UI
  ========================================================= */
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-600 via-blue-50 to-white">

      {/* HEADER */}
      <div className="p-4 flex items-center gap-3 bg-blue-600 text-white shadow-md">
        <img
          src={
            ride.workerId?.profilePhotoUrl
              ? `${API_BASE}${ride.workerId.profilePhotoUrl}`
              : "/default-avatar.png"
          }
          className="w-12 h-12 rounded-full border-2 border-white shadow"
          alt="Driver"
        />
        <div>
          <div className="text-lg font-bold">{ride.workerId?.name}</div>
          <div className="text-sm opacity-90">
            {isStop ? stopFrom : ride.from} → {isStop ? stopTo : ride.to}
          </div>
        </div>
      </div>

      {/* STATUS BAR */}
      <div className="flex items-center justify-between bg-white p-3 shadow-sm">
        {bookingButton()}
         <button
  onClick={() =>
    navigate(`/customer/ride-preview/${rideId}`, {
      state: {
        from: isStop ? stopFrom : ride.from,
        to: isStop ? stopTo : ride.to,
        finalPrice: isStop ? stopPrice : ride.pricePerSeat,
        isStop: isStop
      }
    })
  }
  className="px-4 py-2 rounded-full bg-gray-200 text-gray-700 font-medium hover:bg-gray-300"
>
  View Details
</button>

      </div>

      {/* MESSAGES */}
      <div className="flex-1 p-4 overflow-y-auto space-y-3">
        {messages.map((m, i) => {
          const mine =
            m.senderModel === "User" &&
            normalizeId(m.sender) === normalizeId(customerId);

          return (
            <div
              key={i}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[75%] px-4 py-2 rounded-2xl shadow text-sm ${
                  mine
                    ? "bg-blue-600 text-white rounded-br-none ml-3"
                    : "bg-white text-gray-800 rounded-bl-none mr-3 border"
                }`}
              >
                {m.text}
                <div className="text-[10px] opacity-70 mt-1 text-right">
                  {m.timestamp ? format(new Date(m.timestamp), "p") : ""}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* INPUT BAR */}
      <div className="p-3 bg-white shadow-lg flex gap-2">
        <input
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          className="flex-1 bg-gray-100 border rounded-full px-4 py-3 focus:outline-none"
          placeholder="Message driver…"
        />
        <button
          onClick={sendMessage}
          className="bg-blue-600 text-white px-5 py-3 rounded-full shadow hover:bg-blue-700"
        >
          ➤
        </button>
      </div>

      {/* MODAL */}
      {showModal && (
        <RideBookingModal
          onClose={() => setShowModal(false)}
          rideId={rideId}
          from={isStop ? stopFrom : ride.from}
          to={isStop ? stopTo : ride.to}
          finalPrice={isStop ? stopPrice : ride.pricePerSeat}
        />
      )}
    </div>
  );
};

export default RideChatPage;
